import { useState } from 'react';

interface PracticeQuizProps {
  questions: Array<{
    id: number;
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
  }>;
  solidName: string;
}

export default function PracticeQuiz({ questions, solidName }: PracticeQuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<number[]>([]);

  const handleAnswerSelect = (answerIndex: number) => {
    if (showExplanation) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;

    setShowExplanation(true);
    if (selectedAnswer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
    setAnsweredQuestions([...answeredQuestions, currentQuestion]);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const handleReset = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setAnsweredQuestions([]);
  };

  const question = questions[currentQuestion];
  const isCorrect = selectedAnswer === question.correctAnswer;
  const progress = ((answeredQuestions.length) / questions.length) * 100;

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <i className="ri-question-answer-line text-xl"></i>
          Practice Questions - {solidName}
        </h3>
        <div className="text-sm font-semibold text-slate-600">
          Score: {score}/{answeredQuestions.length}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-600">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-xs font-semibold text-slate-600">
            {progress.toFixed(0)}% Complete
          </span>
        </div>
        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border-2 border-slate-200 mb-4">
          <p className="text-base font-semibold text-slate-800">{question.question}</p>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrectAnswer = index === question.correctAnswer;
            
            let bgColor = 'bg-white hover:bg-slate-50';
            let borderColor = 'border-slate-300';
            let textColor = 'text-slate-800';

            if (showExplanation) {
              if (isCorrectAnswer) {
                bgColor = 'bg-emerald-50';
                borderColor = 'border-emerald-500';
                textColor = 'text-emerald-900';
              } else if (isSelected && !isCorrect) {
                bgColor = 'bg-rose-50';
                borderColor = 'border-rose-500';
                textColor = 'text-rose-900';
              }
            } else if (isSelected) {
              bgColor = 'bg-teal-50';
              borderColor = 'border-teal-500';
              textColor = 'text-teal-900';
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={showExplanation}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${bgColor} ${borderColor} ${textColor} ${
                  showExplanation ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    showExplanation && isCorrectAnswer
                      ? 'bg-emerald-500 border-emerald-600'
                      : showExplanation && isSelected && !isCorrect
                      ? 'bg-rose-500 border-rose-600'
                      : isSelected
                      ? 'bg-teal-500 border-teal-600'
                      : 'border-slate-400'
                  }`}>
                    {showExplanation && isCorrectAnswer && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                    {showExplanation && isSelected && !isCorrect && (
                      <i className="ri-close-line text-white text-sm"></i>
                    )}
                    {!showExplanation && isSelected && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <span className="text-sm font-medium">{option}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Explanation */}
      {showExplanation && (
        <div className={`mb-6 p-4 rounded-lg border-2 ${
          isCorrect
            ? 'bg-emerald-50 border-emerald-300'
            : 'bg-rose-50 border-rose-300'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isCorrect ? 'bg-emerald-500' : 'bg-rose-500'
            }`}>
              <i className={`${isCorrect ? 'ri-check-line' : 'ri-close-line'} text-white text-lg`}></i>
            </div>
            <div className="flex-1">
              <p className={`text-sm font-bold mb-2 ${
                isCorrect ? 'text-emerald-900' : 'text-rose-900'
              }`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </p>
              <p className="text-sm text-slate-700">
                <strong>Explanation:</strong> {question.explanation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
            currentQuestion === 0
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-slate-600 text-white hover:bg-slate-700 cursor-pointer'
          }`}
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Previous
        </button>

        {!showExplanation ? (
          <button
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
            className={`px-6 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
              selectedAnswer === null
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700 cursor-pointer'
            }`}
          >
            Submit Answer
          </button>
        ) : currentQuestion < questions.length - 1 ? (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg font-semibold text-sm hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
          >
            Next Question
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        ) : (
          <button
            onClick={handleReset}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-restart-line mr-2"></i>
            Start Over
          </button>
        )}

        <button
          onClick={handleNext}
          disabled={currentQuestion === questions.length - 1}
          className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
            currentQuestion === questions.length - 1
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : 'bg-slate-600 text-white hover:bg-slate-700 cursor-pointer'
          }`}
        >
          Next
          <i className="ri-arrow-right-line ml-2"></i>
        </button>
      </div>

      {/* Final Score */}
      {answeredQuestions.length === questions.length && (
        <div className="mt-6 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-300">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-trophy-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-indigo-900 mb-2">Quiz Complete!</h4>
            <p className="text-3xl font-bold text-indigo-600 mb-2">
              {score} / {questions.length}
            </p>
            <p className="text-sm text-slate-600 mb-4">
              {score === questions.length
                ? 'Perfect score! You\'ve mastered this topic! 🎉'
                : score >= questions.length * 0.8
                ? 'Great job! You have a strong understanding! 👏'
                : score >= questions.length * 0.6
                ? 'Good effort! Review the explanations to improve. 📚'
                : 'Keep practicing! Review the concepts and try again. 💪'}
            </p>
            <button
              onClick={handleReset}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-restart-line mr-2"></i>
              Retake Quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}